/**
 * Disk Space Step Component
 *
 * Shows resolved components and their disk space requirements.
 * Provides a summary of total vs available disk space and install/skip actions.
 * Allows users to change the install location for large components.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  configureComponent,
  setComponentInstallPath,
  getComponentInstallPath,
} from '@/services/setupService';
import { selectComponentPath, isElectronEnvironment } from '@/services/localComponentService';

/** Minimum disk size (GB) to show the "Change Location" button */
const LARGE_COMPONENT_THRESHOLD_GB = 1;

interface ComponentRequirement {
  id: string;
  name: string;
  description?: string;
  diskGb: number;
  requiresGpu: boolean;
  cloudAvailable: boolean;
  alreadyInstalled: boolean;
}

interface DiskSpaceStepProps {
  components: ComponentRequirement[];
  optionalComponents?: ComponentRequirement[];
  selectedOptionalIds?: string[];
  onToggleOptional?: (id: string) => void;
  totalDiskGb: number;
  availableDiskGb: number;
  sufficient: boolean;
  isLoading: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

function CheckIcon(): React.ReactElement {
  return (
    <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SpinnerIcon(): React.ReactElement {
  return (
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4" />
  );
}

interface BadgeProps {
  label: string;
  variant: 'purple' | 'blue';
}

function Badge({ label, variant }: BadgeProps): React.ReactElement {
  const classes =
    variant === 'purple'
      ? 'bg-purple-900/40 text-purple-300 border border-purple-500/30'
      : 'bg-blue-900/40 text-blue-300 border border-blue-500/30';

  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${classes}`}>{label}</span>;
}

interface ComponentRowProps {
  component: ComponentRequirement;
  installPath: string | null;
  onChangePath: (componentId: string) => void;
  isElectron: boolean;
  t: (key: string, params?: Record<string, string | number | boolean>) => string;
}

function ComponentRow({
  component,
  installPath,
  onChangePath,
  isElectron,
  t,
}: ComponentRowProps): React.ReactElement {
  const isLarge = component.diskGb >= LARGE_COMPONENT_THRESHOLD_GB;
  const showPathPicker = isLarge && !component.alreadyInstalled && isElectron;

  return (
    <div className="p-3 rounded-lg bg-black/20 border border-white/5 space-y-2">
      <div className="flex items-center justify-between gap-3">
        {/* Left: name + badges */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-white/90 truncate">{component.name}</span>
            {component.requiresGpu && (
              <Badge
                label={t('wizard.diskSpace.badge.gpu', { defaultValue: 'GPU' })}
                variant="purple"
              />
            )}
            {component.cloudAvailable && (
              <Badge
                label={t('wizard.diskSpace.badge.cloudFallback', {
                  defaultValue: 'Cloud fallback',
                })}
                variant="blue"
              />
            )}
          </div>
          {component.description && (
            <p className="text-xs text-white/40 mt-0.5">{component.description}</p>
          )}
        </div>

        {/* Right: size + installed indicator */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {component.alreadyInstalled ? (
            <div className="flex items-center gap-1.5">
              <CheckIcon />
              <span className="text-xs text-green-400">
                {t('wizard.diskSpace.installed', { defaultValue: 'Installed' })}
              </span>
            </div>
          ) : (
            <span className="text-sm text-white/50 tabular-nums">
              {component.diskGb.toFixed(1)} {t('wizard.diskSpace.gb', { defaultValue: 'GB' })}
            </span>
          )}
        </div>
      </div>

      {/* Install path row - only for large, uninstalled components in Electron */}
      {showPathPicker && (
        <div className="flex items-center gap-2 ml-0.5">
          <span className="text-xs text-white/40 flex-shrink-0">
            {t('wizard.diskSpace.installTo', { defaultValue: 'Install to:' })}
          </span>
          <span className="text-xs text-white/60 font-mono truncate flex-1 min-w-0">
            {installPath || t('wizard.diskSpace.defaultPath', { defaultValue: 'Default location' })}
          </span>
          <button
            onClick={() => onChangePath(component.id)}
            className="text-xs text-primary-400 hover:text-primary-300 transition-colors flex-shrink-0 min-h-[32px] px-2"
          >
            {t('wizard.diskSpace.changeLocation', { defaultValue: 'Change' })}
          </button>
        </div>
      )}
    </div>
  );
}

interface OptionalComponentRowProps {
  component: ComponentRequirement;
  selected: boolean;
  onToggle: (id: string) => void;
  t: (key: string, params?: Record<string, string | number | boolean>) => string;
}

function OptionalComponentRow({
  component,
  selected,
  onToggle,
  t,
}: OptionalComponentRowProps): React.ReactElement {
  const disabled = component.alreadyInstalled;

  return (
    <label
      className={`p-3 rounded-lg border flex items-center justify-between gap-3 cursor-pointer transition-colors ${
        disabled
          ? 'bg-black/10 border-white/5 opacity-60 cursor-not-allowed'
          : selected
            ? 'bg-primary-900/20 border-primary-500/40'
            : 'bg-black/20 border-white/10 hover:border-white/20'
      }`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <input
          type="checkbox"
          checked={selected || disabled}
          disabled={disabled}
          onChange={() => !disabled && onToggle(component.id)}
          className="w-4 h-4 accent-primary-500 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-white/90 truncate">{component.name}</span>
            {component.requiresGpu && (
              <Badge
                label={t('wizard.diskSpace.badge.gpu', { defaultValue: 'GPU' })}
                variant="purple"
              />
            )}
            {component.cloudAvailable && (
              <Badge
                label={t('wizard.diskSpace.badge.cloudFallback', {
                  defaultValue: 'Cloud fallback',
                })}
                variant="blue"
              />
            )}
          </div>
          {component.description && (
            <p className="text-xs text-white/40 mt-0.5">{component.description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        {component.alreadyInstalled ? (
          <div className="flex items-center gap-1.5">
            <CheckIcon />
            <span className="text-xs text-green-400">
              {t('wizard.diskSpace.installed', { defaultValue: 'Installed' })}
            </span>
          </div>
        ) : (
          <span className="text-sm text-white/50 tabular-nums">
            +{component.diskGb.toFixed(1)} {t('wizard.diskSpace.gb', { defaultValue: 'GB' })}
          </span>
        )}
      </div>
    </label>
  );
}

interface DiskSummaryBarProps {
  totalDiskGb: number;
  availableDiskGb: number;
  sufficient: boolean;
  t: (key: string, params?: Record<string, string | number | boolean>) => string;
}

function DiskSummaryBar({
  totalDiskGb,
  availableDiskGb,
  sufficient,
  t,
}: DiskSummaryBarProps): React.ReactElement {
  const usagePercent =
    availableDiskGb > 0 ? Math.min((totalDiskGb / availableDiskGb) * 100, 100) : 100;
  const barColor = sufficient ? 'bg-green-500' : 'bg-red-500';

  return (
    <div
      className={`rounded-xl p-4 border ${
        sufficient ? 'bg-black/20 border-white/10' : 'bg-red-900/20 border-red-500/30'
      }`}
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="text-sm text-white/60">
          {t('wizard.diskSpace.totalRequired', { defaultValue: 'Total required:' })}{' '}
          <span className="text-white font-semibold">
            {totalDiskGb.toFixed(1)} {t('wizard.diskSpace.gb', { defaultValue: 'GB' })}
          </span>
        </span>
        <span className="text-sm text-white/60">
          {t('wizard.diskSpace.available', { defaultValue: 'Available:' })}{' '}
          <span className={`font-semibold ${sufficient ? 'text-green-400' : 'text-red-400'}`}>
            {availableDiskGb.toFixed(1)} {t('wizard.diskSpace.gb', { defaultValue: 'GB' })}
          </span>
        </span>
      </div>

      {/* Usage bar */}
      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${usagePercent}%` }}
        />
      </div>

      {/* Warning message */}
      {!sufficient && (
        <p className="mt-3 text-sm text-red-300">
          {t('wizard.diskSpace.insufficientWarning', {
            defaultValue: 'You may not have enough disk space. Some installations may fail.',
          })}
        </p>
      )}
    </div>
  );
}

export function DiskSpaceStep({
  components,
  optionalComponents = [],
  selectedOptionalIds = [],
  onToggleOptional,
  totalDiskGb,
  availableDiskGb,
  sufficient,
  isLoading,
  onNext,
  onBack,
  onSkip,
}: DiskSpaceStepProps): React.ReactElement {
  const { t } = useTranslation();
  const isElectron = isElectronEnvironment();

  // Track custom install paths per component
  const [installPaths, setInstallPaths] = useState<Record<string, string | null>>({});

  // Load existing paths for large components on mount
  useEffect(() => {
    const loadPaths = async () => {
      for (const comp of components) {
        if (comp.diskGb >= LARGE_COMPONENT_THRESHOLD_GB && !comp.alreadyInstalled) {
          try {
            const pathInfo = await getComponentInstallPath(comp.id);
            if (pathInfo.customPath || pathInfo.effectivePath) {
              setInstallPaths((prev) => ({
                ...prev,
                [comp.id]: pathInfo.customPath || pathInfo.effectivePath,
              }));
            }
          } catch {
            // Ignore - will show default
          }
        }
      }
    };
    if (components.length > 0) {
      loadPaths();
    }
  }, [components]);

  // Handle changing install path for a component
  const handleChangePath = useCallback(async (componentId: string) => {
    try {
      // Use Electron's native folder picker
      const result = await selectComponentPath(componentId);
      if (result && result.path) {
        // Save the path to backend
        await setComponentInstallPath(componentId, result.path);
        // Also configure as local install with this path
        await configureComponent(componentId, 'local', result.path);
        setInstallPaths((prev) => ({ ...prev, [componentId]: result.path }));
      }
    } catch (error) {
      console.error(`Failed to change path for ${componentId}:`, error);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <SpinnerIcon />
        <h2 className="text-xl font-semibold text-white mb-2">
          {t('wizard.diskSpace.resolving', { defaultValue: 'Resolving components...' })}
        </h2>
        <p className="text-white/50">
          {t('wizard.diskSpace.pleaseWait', { defaultValue: 'Calculating disk requirements' })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="section-heading-xl">
          {t('wizard.diskSpace.title', { defaultValue: 'Required Components' })}
        </h2>
        <p className="text-white/60">
          {t('wizard.diskSpace.subtitle', {
            defaultValue: 'These components will be installed based on your selections',
          })}
        </p>
      </div>

      {/* Component list */}
      {components.length > 0 ? (
        <div className="space-y-2">
          {components.map((component) => (
            <ComponentRow
              key={component.id}
              component={component}
              installPath={installPaths[component.id] ?? null}
              onChangePath={handleChangePath}
              isElectron={isElectron}
              t={t}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-white/40">
          <p>{t('wizard.diskSpace.noComponents', { defaultValue: 'No components to install.' })}</p>
        </div>
      )}

      {/* Optional components */}
      {optionalComponents.length > 0 && onToggleOptional && (
        <div className="space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-white/80">
              {t('wizard.diskSpace.optional.title', { defaultValue: 'Optional components' })}
            </h3>
            <p className="text-xs text-white/50">
              {t('wizard.diskSpace.optional.subtitle', {
                defaultValue:
                  'Extra tools recommended for this use case. Check to include them in the install.',
              })}
            </p>
          </div>
          {optionalComponents.map((component) => (
            <OptionalComponentRow
              key={component.id}
              component={component}
              selected={selectedOptionalIds.includes(component.id)}
              onToggle={onToggleOptional}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Disk summary */}
      <DiskSummaryBar
        totalDiskGb={
          totalDiskGb +
          optionalComponents
            .filter((c) => selectedOptionalIds.includes(c.id) && !c.alreadyInstalled)
            .reduce((sum, c) => sum + c.diskGb, 0)
        }
        availableDiskGb={availableDiskGb}
        sufficient={sufficient}
        t={t}
      />

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onBack}
          className="px-4 py-2 text-white/50 hover:text-white/80 transition-colors min-h-[44px]"
        >
          {t('wizard.back', { defaultValue: 'Back' })}
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={onSkip}
            className="px-4 py-2 text-white/50 hover:text-white/80 transition-colors text-sm min-h-[44px]"
          >
            {t('wizard.skip', { defaultValue: 'Skip' })}
          </button>
          <button
            onClick={onNext}
            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors min-h-[44px]"
          >
            {t('wizard.diskSpace.install', { defaultValue: 'Install' })}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DiskSpaceStep;
